import { useLoginMutation } from "@src/data/mutations/login";
import { version } from "@src/env";
import type { LoginFormData } from "@src/types";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "@src/components/Button";
import { Checkbox } from "@src/components/Checkbox";
import { Download } from "@src/components/Download";
import { FormGroup } from "@src/components/FormGroup";
import { Input } from "@src/components/Input";
import { Loader } from "@src/components/Loader";

import { useStateWithQueryString } from "@src/hooks/useStateWithQueryString";

import { useMeuPlayState } from "@src/states/meuplay";

import { dispatchEventToNative } from "@src/utils/dispatchEventToNative";
import { formDataToObject } from "@src/utils/formDataToObject";
import { formatCnpj } from "@src/utils/formatCnpj";
import { hardReset } from "@src/utils/serviceWorker/hardReset";
import {
  type MemorizedSession,
  forgetSession,
  getMemorizedSessions,
  isAutoLoginSuppressed,
  rememberSession,
  sessionTitle,
  suppressAutoLogin,
  takePendingSwitch,
} from "@src/utils/sessions";

import "./style.less";

/** Segundos até o auto-login quando há mais de uma sessão memorizada. */
const AUTO_LOGIN_SECONDS = 60;

type AutoPlan =
  | { mode: "none" }
  /** Sessão única: entra direto. */
  | { mode: "now"; session: MemorizedSession }
  /** Várias sessões: conta regressiva até entrar na usada mais recentemente. */
  | { mode: "countdown"; session: MemorizedSession };

export function Login() {
  const [liteMode, setLiteMode] = useStateWithQueryString("0", "lite");

  const login = useLoginMutation();

  // Atualiza a rádio a partir do loginFormData
  const updated = useRef(false);
  const { loginFormData, radio } = useMeuPlayState();
  const navigate = useNavigate();
  const { search, state } = useLocation();

  // Plano de auto-login, decidido uma única vez na montagem (lê storage).
  // Declarado antes do efeito de revalidação porque o bloqueia numa troca.
  const [autoPlan] = useState<AutoPlan>(() => {
    // Troca pedida no player tem prioridade sobre tudo: é ação explícita do
    // usuário, então vale mesmo com a trava de auto-login já usada nesta aba.
    const pending = takePendingSwitch();
    if (pending) {
      const target = getMemorizedSessions().find((s) => s.key === pending);
      if (target) return { mode: "now", session: target };
    }
    // Já há sessão em memória (revalidação) ou autoSubmit vindo de deep link:
    // o fluxo existente já entra sozinho — não concorrer com ele.
    if (loginFormData || state?.autoSubmit || isAutoLoginSuppressed()) {
      return { mode: "none" };
    }
    // A lista já vem da mais recente para a mais antiga, então o alvo é sempre
    // a primeira. Numa base que ainda não rodou esta versão ninguém tem
    // `lastUsedAt`: o desempate por rótulo escolhe uma de forma determinística,
    // e o primeiro uso já reordena.
    const memorized = getMemorizedSessions();
    if (memorized.length === 0) return { mode: "none" };
    if (memorized.length === 1) return { mode: "now", session: memorized[0] };
    return { mode: "countdown", session: memorized[0] };
  });

  useEffect(() => {
    if (updated.current) {
      // Já atualizou a rádio
      if (loginFormData && radio) {
        const url = radio.require_cnpj
          ? `/${radio.name}/${loginFormData.cnpj}${search}`
          : `/${radio.name}${search}`;
        console.log("[Login] Navegando para:", url);
        navigate(url, { replace: true, state: { updated: true } });
      }
      return;
    }
    updated.current = true;
    // Só a revalidação inicial é pulada quando há auto-login em curso: o estado
    // ainda pode conter a rádio anterior (o logout é assíncrono) e revalidá-la
    // traria o usuário de volta para ela. A navegação acima continua valendo —
    // é ela que leva ao player quando o auto-login conclui.
    if (autoPlan.mode !== "none") return;
    if (loginFormData && radio) {
      login.mutateAsync(loginFormData).then(
        (updatedRadio) => {
          useMeuPlayState.setState({ radio: updatedRadio });
          // Retomar a sessão também conta como uso: num aparelho que só é
          // reiniciado, este é o único caminho percorrido por meses — sem o
          // bump aqui, uma rádio em uso diário perderia o auto-login para
          // outra que foi logada uma única vez e abandonada.
          rememberSession(loginFormData, updatedRadio);
          console.log("[Login] Rádio atualizada para:", updatedRadio);

          login.reset(); // login.isPending = false;
        },
        (error) => {
          console.log("[Login] Falha ao atualizar a rádio:", error);
        },
      );
    }
  }, [loginFormData, radio, login, navigate, search, autoPlan]);

  const doLogin = useCallback(
    (loginFormData: LoginFormData) => {
      login.mutateAsync(loginFormData).then(
        (radio) => {
          dispatchEventToNative("login", loginFormData);

          useMeuPlayState.setState({ loginFormData, radio });
          // Memoriza fora do estado do zustand: o logout limparia a gaveta.
          rememberSession(loginFormData, radio);
          if (radio) {
            console.log("[Login] Rádio definida como:", radio);
          }
        },
        (error) => {
          console.log("[Login] Falha ao definir a rádio:", error);
        },
      );
    },
    [login],
  );

  const submitForm = useCallback(
    (form: HTMLFormElement) => {
      const loginFormData = formDataToObject<LoginFormData>(new FormData(form));
      if (Object.keys(loginFormData).length === 0) {
        return;
      }

      // Remove non-numeric characters from CNPJ
      loginFormData.cnpj = loginFormData.cnpj.replace(/\D/g, "");

      doLogin(loginFormData);
    },
    [doLogin],
  );

  // ─── Sessões memorizadas + auto-login ─────────────────────────────────────
  const [sessions, setSessions] = useState(getMemorizedSessions);

  const [countdown, setCountdown] = useState<number | null>(
    autoPlan.mode === "countdown" ? AUTO_LOGIN_SECONDS : null,
  );

  const autoFired = useRef(false);
  const fireAutoLogin = useCallback(
    (session: MemorizedSession) => {
      if (autoFired.current) return;
      autoFired.current = true;
      suppressAutoLogin();
      console.log("[Login] Auto-login em:", session.key);
      doLogin(session.loginFormData);
    },
    [doLogin],
  );

  /** Qualquer interação do usuário cancela o auto-login desta aba. */
  const cancelAutoLogin = useCallback(() => {
    if (autoFired.current) return;
    setCountdown((current) => {
      if (current === null) return null;
      console.log("[Login] Auto-login cancelado pelo usuário");
      suppressAutoLogin();
      return null;
    });
  }, []);

  // Sessão única: entra sem esperar.
  useEffect(() => {
    if (autoPlan.mode === "now") fireAutoLogin(autoPlan.session);
  }, [autoPlan, fireAutoLogin]);

  // Várias sessões: tique de 1s até o auto-login na rádio escolhida acima.
  useEffect(() => {
    if (countdown === null || autoPlan.mode !== "countdown") return;
    if (countdown <= 0) {
      fireAutoLogin(autoPlan.session);
      return;
    }
    const timeout = setTimeout(() => {
      setCountdown((current) => (current === null ? null : current - 1));
    }, 1_000);
    return () => clearTimeout(timeout);
  }, [countdown, autoPlan, fireAutoLogin]);

  // Caso de uso: ao tentar entrar em /recifeinterna
  // já estando logado como /wishpiscina/07687928000645
  const formRef = useRef<HTMLFormElement | null>(null);
  const submitted = useRef(false);
  useEffect(() => {
    if (submitted.current) return;
    submitted.current = true;

    setTimeout(() => {
      if (formRef.current && state?.autoSubmit) {
        console.log("autoSubmit");
        submitForm(formRef.current);
      }
    });
  }, [submitForm, state?.autoSubmit]);

  let idRadio = state?.idRadio ?? "";
  if (idRadio === "undefined") idRadio = "";

  return (
    <div className="Login-page">
      {liteMode === "0" && (
        <video
          autoPlay
          loop
          muted
          playsInline
          disablePictureInPicture
          controlsList="nodownload"
        >
          <source src="/bg-video-escuro.mp4" type="video/mp4" />
        </video>
      )}
      <form
        ref={formRef}
        className="card"
        onSubmit={(event) => {
          event.preventDefault();
          cancelAutoLogin();
          submitForm(event.currentTarget);
        }}
        onFocusCapture={cancelAutoLogin}
        onInputCapture={cancelAutoLogin}
      >
        <img
          src="/logo.png"
          alt="Fabricca Multimídia"
          title="Fabricca Multimídia"
          className="logo"
        />
        <span style={{ color: "white" }}>v{version}</span>

        {sessions.length > 0 && (
          <div className="sessions">
            <span className="title">Sessões memorizadas</span>
            {sessions.map((session) => {
              // A contagem regressiva mora no próprio card do alvo: é o mesmo
              // botão que o usuário clicaria, então não precisa repetir a rádio
              // num aviso à parte.
              const entrandoEm =
                countdown !== null &&
                autoPlan.mode === "countdown" &&
                autoPlan.session.key === session.key
                  ? countdown
                  : null;
              return (
                <div key={session.key} className="session">
                  <Button
                    type="button"
                    white
                    className={clsx("enter", { target: entrandoEm !== null })}
                    disabled={login.isPending}
                    title={`Entrar em ${sessionTitle(session)}`}
                    onClick={() => {
                      cancelAutoLogin();
                      doLogin(session.loginFormData);
                    }}
                  >
                    <span className="name">{session.label}</span>
                    {session.detail && (
                      <span className="detail">{session.detail}</span>
                    )}
                    {entrandoEm !== null && (
                      <span className="countdown">
                        entrando em {entrandoEm}s
                      </span>
                    )}
                  </Button>
                  <Button
                    type="button"
                    white
                    className="forget"
                    title={`Esquecer ${sessionTitle(session)}`}
                    onClick={() => {
                      cancelAutoLogin();
                      forgetSession(session.key);
                      setSessions(getMemorizedSessions());
                    }}
                  >
                    ✕
                  </Button>
                </div>
              );
            })}
            {countdown !== null && autoPlan.mode === "countdown" && (
              // Ação de escape: link discreto, no padrão dos demais links da
              // tela — um botão sólido competiria com os cards de sessão.
              <button
                type="button"
                className="cancel"
                onClick={cancelAutoLogin}
              >
                Cancelar entrada automática
              </button>
            )}
          </div>
        )}

        <fieldset disabled={login.isPending}>
          <FormGroup>
            <label htmlFor="idRadio">Nome da rádio</label>
            <Input
              id="idRadio"
              type="text"
              name="idRadio"
              defaultValue={idRadio}
              autoCapitalize="none"
              required
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="cnpj">CNPJ</label>
            <Input
              id="cnpj"
              type="text"
              name="cnpj"
              autoComplete="username"
              defaultValue={formatCnpj(state?.cnpj || "")}
              onChange={(event) => {
                const element = event.currentTarget;
                element.value = formatCnpj(element.value);
              }}
            />
          </FormGroup>

          <FormGroup>
            <label htmlFor="senha">Senha</label>
            <Input
              id="senha"
              type="password"
              name="senha"
              autoComplete="current-password"
              defaultValue={state?.senha}
              autoCapitalize="none"
            />
          </FormGroup>

          <label className="lite-mode">
            <Checkbox
              checked={liteMode === "1"}
              onChange={(event) =>
                setLiteMode(event.target.checked ? "1" : "0")
              }
            />
            Modo Lite
          </label>

          <Button type="submit" disabled={login.isPending}>
            Logar
          </Button>
          <Button type="button" onClick={() => void hardReset()}>
            Limpar Cache
          </Button>
          {login.isError && (
            <span className="error">
              {login.error.message.includes("not valid JSON") ||
              login.error.message.includes("Failed to fetch")
                ? "API indisponível"
                : login.error.message}
            </span>
          )}
          <Loader visible={login.isPending} />
          <a href="/codecs">Testar Codecs</a>
          <a href="/portal">Portal de Links</a>
          <Download />
        </fieldset>
      </form>
    </div>
  );
}
